import { hasPermission } from "@/lib/permissions";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, Sun, Moon, Check, LogOut, ChevronDown, ChevronRight, Building2, CircleDot, Cloud, CloudOff, User, RefreshCcw, AlertTriangle, Coins, LifeBuoy, Settings2, ShieldCheck, WalletCards, ArrowUpRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SignOutConfirmationDialog } from "@/components/auth/SignOutConfirmationDialog";
import { useAuth, useUi, useWorkspace } from "@/lib/store";
import { formatRelative } from "@/lib/formatters";
import { formatSessionRole } from "@/lib/sessionLabels";
import { useLocalization } from "@/lib/localization";
import { fetchBillingWallet } from "@/lib/shopApi";
import { normalizeDollarSymbol } from "@/lib/money";

const TITLES = {
  "/dashboard": ["dashboard", "dashboardSubtitle"],
  "/sell": ["sell", "sellSubtitle"],
  "/inventory": ["inventory", "inventorySubtitle"],
  "/history": ["history", "historySubtitle"],
  "/debts": ["debts", "debtsSubtitle"],
  "/expenses": ["expenses", "expensesSubtitle"],
  "/credits": ["credits", "creditsSubtitle"],
  "/wallet": ["wallet", "walletSubtitle"],
  "/notifications": ["notifications", "notificationsSubtitle"],
  "/assistant": ["assistant", "assistantSubtitle"],
  "/analytics": ["analytics", "analyticsSubtitle"],
  "/reports": ["reports", "reportsSubtitle"],
  "/staff": ["staff", "staffSubtitle"],
  "/branches": ["branches", "branchesSubtitle"],
  "/settings": ["settings", "settingsSubtitle"],
  "/support": ["support", "supportSubtitle"],
};

export default function Topbar({ mobileSidebarOpen = false, onMobileSidebarToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, messages } = useLocalization();
  const copy = messages.layout;
  const { session } = useAuth();
  const { theme, toggleTheme } = useUi();
  const {
    branches,
    credits,
    currentBranch,
    loading,
    markAllNotificationsRead,
    markNotificationRead,
    notifications,
    reload,
    switchBranch,
    sync,
    unreadNotifications,
  } = useWorkspace();
  const matchedTitle = Object.entries(TITLES).find(([path]) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  const [titleKey, subtitleKey] = matchedTitle?.[1] || ["", ""];
  const title = copy[titleKey] || "";
  const subtitle = copy[subtitleKey] || "";
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [billingWallet, setBillingWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const unread = unreadNotifications?.length ?? 0;
  const notificationPreview = unread > 0 ? unreadNotifications.slice(0, 5) : (notifications?.inbox ?? []).slice(0, 5);
  const hasSyncIssues = Boolean(sync.failedCount || sync.lastError);
  const availableCredits = credits?.availableCredits || "0";
  const canManageBilling = hasPermission(session, "billing");
  const walletBalance = billingWallet?.wallet;
  const formatWalletBalance = () => {
    if (!walletBalance) return "—";
    try {
      return normalizeDollarSymbol(new Intl.NumberFormat(locale, { currency: walletBalance.currencyCode, currencyDisplay: "narrowSymbol", style: "currency" }).format(walletBalance.availableMinor / 100));
    } catch {
      return `${walletBalance.currencyCode} ${(walletBalance.availableMinor / 100).toFixed(2)}`;
    }
  };

  function loadWalletOnOpen(open) {
    if (!open || !canManageBilling || !session?.sessionId) return;
    setWalletLoading(true);
    void fetchBillingWallet(session.sessionId)
      .then(setBillingWallet)
      .catch(() => setBillingWallet(null))
      .finally(() => setWalletLoading(false));
  }

  return (
    <header className="sticky top-0 z-20 h-14 bg-background/70 backdrop-blur-2xl border-b border-border/70">
      <div className="h-full px-6 lg:px-8 flex items-center gap-3">
        {/* Mobile navigation — occupies the sidebar's visual anchor when it is hidden. */}
        <button
          type="button"
          data-testid="mobile-sidebar-toggle"
          onClick={onMobileSidebarToggle}
          className="group relative grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-[10px] border border-primary/15 bg-primary/[0.07] text-primary shadow-[0_8px_24px_-16px_hsl(var(--primary))] transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:bg-primary/[0.12] hover:shadow-[0_10px_26px_-14px_hsl(var(--primary))] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 lg:hidden"
          aria-label={mobileSidebarOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={mobileSidebarOpen}
          aria-controls="mobile-workspace-navigation"
        >
          <span className="pointer-events-none absolute -right-2 -top-2 h-5 w-5 rounded-full bg-primary/15 transition-transform duration-300 group-hover:scale-150" />
          <span className="relative flex h-3.5 w-4 flex-col justify-between" aria-hidden="true">
            <span className={`h-[1.5px] rounded-full bg-current transition-all duration-300 ${mobileSidebarOpen ? "translate-y-[6px] rotate-45" : "w-4"}`} />
            <span className={`h-[1.5px] rounded-full bg-current transition-all duration-200 ${mobileSidebarOpen ? "scale-x-0 opacity-0" : "ml-auto w-2.5"}`} />
            <span className={`h-[1.5px] rounded-full bg-current transition-all duration-300 ${mobileSidebarOpen ? "-translate-y-[6px] -rotate-45" : "w-3.5"}`} />
          </span>
        </button>

        {/* Title */}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-3">
            <h1 data-testid="page-title" className="text-[16px] font-semibold tracking-[-0.01em] truncate">
              {title}
            </h1>
            <span className="text-xs text-muted-foreground hidden md:inline tracking-[0.01em]">{subtitle}</span>
          </div>
        </div>

        {/* Branch switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="topbar-branch-switcher"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-border/70 hover:bg-accent hover:border-border-strong transition-all text-[13px] font-medium disabled:pointer-events-none disabled:opacity-60"
              disabled={loading || !branches.length}
            >
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="truncate max-w-[160px]">{currentBranch?.name ?? copy.noBranchSelected}</span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
              {copy.switchBranch}
            </DropdownMenuLabel>
            {!branches.length && (
              <DropdownMenuItem disabled className="py-2.5 text-muted-foreground">
                {copy.noBranchesAvailable}
              </DropdownMenuItem>
            )}
            {branches.map((b) => (
              <DropdownMenuItem
                key={b.id}
                data-testid={`branch-option-${b.id}`}
                onClick={() => switchBranch(b.id)}
                className="gap-3 py-2.5"
              >
                <CircleDot className={`h-3 w-3 ${b.id === currentBranch?.id ? "text-success" : "text-muted-foreground"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{b.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {b.city} · {b.staffCount} {copy.staffLabel} · {b.productCount} SKUs
                  </div>
                </div>
                {b.id === currentBranch?.id && <Check className="h-4 w-4 text-primary" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/branches")} className="text-sm text-muted-foreground">
              {copy.manageBranches}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Credits and billing wallet */}
        <DropdownMenu onOpenChange={loadWalletOnOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className="hidden md:inline-flex items-center gap-2 rounded-lg border border-emerald-200/80 bg-emerald-50/70 px-3 h-9 text-[12px] text-emerald-900 transition-colors hover:bg-emerald-100/80"
              title={`${messages.common.availableBalance}: ${availableCredits} ${messages.common.credits.toLowerCase()}`}
              aria-label="Open Shopbook balances"
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/85 text-emerald-700 shadow-sm"><Coins className="h-3.5 w-3.5" /></span>
              <span className="hidden lg:inline font-medium text-emerald-800/90">{messages.common.credits}</span>
              <span className="font-semibold tabular-nums">{availableCredits}</span>
              <ChevronDown className="h-3 w-3 text-emerald-700/60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            collisionPadding={12}
            className="w-[360px] overflow-hidden rounded-[24px] border border-border/80 bg-card p-0 shadow-[0_28px_70px_-28px_rgba(15,42,38,0.38)]"
          >
            <div className="bg-gradient-to-br from-[hsl(184,72%,20%)] to-[hsl(178,64%,29%)] px-5 pb-5 pt-5 text-white">
              <div className="text-[10px] font-semibold uppercase leading-4 tracking-[0.2em] text-white/60">Shopbook balances</div>
              <div className="mt-1.5 text-[20px] font-semibold leading-7 tracking-[-0.02em]">Value, clearly separated</div>
              <div className="mt-1.5 text-[12px] leading-5 text-white/65">AI usage and subscription funds stay independent.</div>
            </div>
            <div className="space-y-3 bg-card p-4">
              <DropdownMenuItem
                disabled={!canManageBilling}
                onSelect={() => { if (canManageBilling) navigate("/credits"); }}
                className="group grid min-h-[84px] w-full cursor-pointer grid-cols-[44px_minmax(0,1fr)_18px] items-center gap-x-4 rounded-[22px] border border-border/70 bg-background px-4 py-4 text-left transition-colors focus:border-primary/25 focus:bg-primary/[0.035]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-[15px] bg-emerald-500/10 text-emerald-700"><Sparkles className="h-[18px] w-[18px]" /></span>
                <span className="min-w-0">
                  <span className="block text-[12px] leading-4 text-muted-foreground">Credits</span>
                  <span className="mt-1 block truncate text-[22px] font-semibold leading-[26px] tracking-[-0.02em] tabular-nums">{availableCredits}</span>
                </span>
                <span className="flex h-[18px] w-[18px] items-center justify-center text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
                  <ArrowUpRight className="h-[18px] w-[18px]" />
                </span>
              </DropdownMenuItem>
              {canManageBilling ? (
                <DropdownMenuItem
                  onSelect={() => { if (canManageBilling) navigate("/wallet"); }}
                  className="group grid min-h-[84px] w-full cursor-pointer grid-cols-[44px_minmax(0,1fr)_18px] items-center gap-x-4 rounded-[22px] border border-transparent bg-[hsl(184,62%,22%)] px-4 py-4 text-left text-white transition-colors focus:border-white/10 focus:bg-[hsl(184,62%,25%)] focus:text-white"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-[15px] bg-white/10"><WalletCards className="h-[18px] w-[18px]" /></span>
                  <span className="min-w-0">
                    <span className="block text-[12px] leading-4 text-white/65">Subscription wallet</span>
                    <span className="mt-1 block truncate whitespace-nowrap text-[22px] font-semibold leading-[26px] tracking-[-0.02em] tabular-nums">{walletLoading ? "•••" : formatWalletBalance()}</span>
                  </span>
                  <span className="flex h-[18px] w-[18px] items-center justify-center text-white/60 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
                    <ArrowUpRight className="h-[18px] w-[18px]" />
                  </span>
                </DropdownMenuItem>
              ) : null}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Sync indicator */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="topbar-sync-indicator"
              className="hidden md:inline-flex items-center gap-1.5 rounded-lg px-2.5 h-9 text-xs text-muted-foreground hover:bg-accent transition-colors"
              title={sync.online ? `${copy.lastSynced} ${formatRelative(sync.lastSyncedAt, locale)}` : copy.offline}
            >
              {sync.online ? (
                <Cloud className="h-3.5 w-3.5 text-success" />
              ) : (
                <CloudOff className="h-3.5 w-3.5 text-destructive" />
              )}
              <span className="font-medium tracking-wide">{sync.online ? copy.synced : copy.offline}</span>
              {(sync.queuedCount > 0 || hasSyncIssues) && (
                <Badge variant="outline" className="ml-1 h-5 px-1.5 text-[10px]">
                  {hasSyncIssues ? copy.attention : `${sync.queuedCount} ${copy.queued}`}
                </Badge>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 border-transparent shadow-[0_24px_60px_-24px_rgba(15,23,42,0.28)]">
            <div className="px-3 py-2.5">
              <div className="flex items-center gap-2">
                {sync.online ? (
                  <Cloud className="h-4 w-4 text-success" />
                ) : (
                  <CloudOff className="h-4 w-4 text-destructive" />
                )}
                <div className="text-sm font-semibold">{sync.online ? copy.workspaceSynced : copy.offlineMode}</div>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {sync.lastSyncedAt ? `${copy.lastSync} ${formatRelative(sync.lastSyncedAt, locale)}` : copy.noCompletedSync}
              </div>
            </div>
            <DropdownMenuSeparator />
            <div className="px-3 py-2 space-y-2 text-xs text-muted-foreground">
              <div className="flex items-center justify-between gap-4">
                <span>{copy.queue}</span>
                <span className="font-medium text-foreground">{sync.queuedCount}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span>{copy.failures}</span>
                <span className={`font-medium ${sync.failedCount ? "text-destructive" : "text-foreground"}`}>{sync.failedCount}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span>{copy.processing}</span>
                <span className="font-medium text-foreground">{sync.isProcessing ? copy.yes : copy.no}</span>
              </div>
              {sync.lastError && (
                <div className="rounded-md border border-destructive/20 bg-destructive/5 px-2.5 py-2 text-destructive flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span className="line-clamp-3">{sync.lastError}</span>
                </div>
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => reload()} className="gap-2">
              <RefreshCcw className="h-4 w-4" /> {copy.refreshWorkspace}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")} className="gap-2 text-muted-foreground">
              <User className="h-4 w-4" /> {copy.openSyncSettings}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme toggle */}
        <Button
          data-testid="topbar-support"
          variant="ghost"
          size="icon"
          onClick={() => navigate("/support")}
          className="h-9 w-9 lg:hidden"
          aria-label={copy.support || "Help and support"}
        >
          <LifeBuoy className="h-4 w-4" />
        </Button>

        <Button
          data-testid="topbar-theme-toggle"
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="h-9 w-9"
          aria-label={copy.toggleTheme(theme === "dark" ? "light" : "dark")}
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="topbar-notifications"
              className="relative h-9 w-9 inline-flex items-center justify-center rounded-lg hover:bg-accent transition-colors overflow-visible"
              aria-label={copy.notifications}
            >
              <Bell className="h-[18px] w-[18px] shrink-0" />
              {unread > 0 && (
                <span className="absolute top-1.5 right-1.5 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center ring-2 ring-background">
                  {unread}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-96">
            <div className="px-3 py-2.5 flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-semibold">{copy.notifications}</div>
                <div className="text-xs text-muted-foreground">
                  {unread > 0 ? copy.unreadMessages(unread) : copy.allCaughtUp}
                </div>
              </div>
              {unread > 0 && (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={markAllNotificationsRead}>
                  {copy.markAllRead}
                </Button>
              )}
            </div>
            <DropdownMenuSeparator />
            {notificationPreview.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {copy.noNotifications}
              </div>
            )}
            {notificationPreview.map((item) => {
              const isUnread = unreadNotifications.some((notification) => notification.id === item.id);
              return (
                <DropdownMenuItem
                  key={item.id}
                  onClick={() => {
                    markNotificationRead(item.id);
                    navigate("/notifications");
                  }}
                  className="items-start gap-3 py-3"
                >
                  <span className={`mt-1 h-2.5 w-2.5 rounded-full ${isUnread ? "bg-primary" : "bg-muted"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate text-sm font-medium">{item.title}</span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">{formatRelative(item.createdAt, locale)}</span>
                    </div>
                    <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{item.body}</div>
                  </div>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/notifications")} className="gap-2">
              <Bell className="h-4 w-4" /> {copy.openNotificationCenter}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Profile menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="topbar-profile"
              className="h-9 w-9 overflow-hidden rounded-full ring-1 ring-border transition-all hover:ring-border-strong hover:shadow-[0_8px_20px_-10px_rgba(15,23,42,0.3)] focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:ring-1 data-[state=open]:ring-border-strong"
            >
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-gradient-to-br from-primary to-[#083f3f] text-primary-foreground text-xs font-semibold">
                  {session?.shopName?.slice(0, 2)?.toUpperCase() ?? "SB"}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8} className="w-[284px] rounded-[18px] border border-border/80 bg-popover/95 p-1.5 shadow-[0_24px_60px_-26px_rgba(15,23,42,0.38)] backdrop-blur-xl focus:outline-none">
            <div className="bg-grain relative overflow-hidden rounded-[14px] bg-[linear-gradient(135deg,#063f38_0%,#0a5b4e_58%,#087060_100%)] p-3.5 text-white">
              <div className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-emerald-200/15 blur-3xl" />
              <div className="relative flex items-center gap-3">
                <Avatar className="h-9 w-9 shrink-0 ring-1 ring-white/20">
                  <AvatarFallback className="bg-white/[0.12] text-sm font-semibold text-white">
                    {session?.shopName?.slice(0, 2)?.toUpperCase() ?? "SB"}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold tracking-[-0.015em]">{session?.shopName}</div>
                  <div className="truncate text-[10px] text-emerald-50/70">{session?.email}</div>
                </div>
                <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-100/70" />
              </div>
              <div className="relative mt-2.5 flex flex-wrap gap-1.5">
                <span className="inline-flex items-center rounded-full bg-white/[0.1] px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.13em] text-emerald-50">
                  {formatSessionRole(session?.role)} {copy.access}
                </span>
                {currentBranch ? (
                  <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full bg-white/[0.1] px-2 py-0.5 text-[9px] text-emerald-50/85">
                    <Building2 className="h-3 w-3 shrink-0" />
                    <span className="max-w-[150px] truncate">{currentBranch.name}{currentBranch.city ? ` · ${currentBranch.city}` : ""}</span>
                  </span>
                ) : null}
              </div>
            </div>

            <div className="space-y-0.5 px-1 py-1.5">
              <ProfileMenuItem icon={Settings2} label={copy.profileSettings} onClick={() => navigate("/settings")} />
              <ProfileMenuItem icon={Bell} label={copy.notificationPreferences} onClick={() => navigate("/notifications")} badge={unread || null} />
              <ProfileMenuItem icon={LifeBuoy} label={copy.support || "Help & support"} onClick={() => navigate("/support")} />
            </div>

            <div className="mx-1 h-px bg-border/70" />
            <DropdownMenuItem
              data-testid="profile-logout"
              onClick={() => setLogoutDialogOpen(true)}
              className="mx-1 my-1 gap-2.5 rounded-xl bg-destructive/[0.045] px-2.5 py-2 text-destructive focus:bg-destructive/10 focus:text-destructive focus:ring-0 focus-visible:ring-0"
            >
              <span className="grid h-7 w-7 place-items-center rounded-lg bg-destructive/10"><LogOut className="h-3.5 w-3.5" /></span>
              <span className="flex-1 text-[13px] font-semibold">{copy.signOut}</span>
              <ChevronRight className="h-3.5 w-3.5 opacity-55" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <SignOutConfirmationDialog
          branchName={currentBranch?.name}
          onOpenChange={setLogoutDialogOpen}
          open={logoutDialogOpen}
        />
      </div>
    </header>
  );
}

function ProfileMenuItem({ icon: Icon, label, onClick, badge }) {
  return (
    <DropdownMenuItem onClick={onClick} className="group gap-2.5 rounded-xl px-2 py-2 focus:bg-primary/[0.065] focus:text-foreground focus:ring-0 focus-visible:ring-0">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-muted/55 text-muted-foreground transition-colors group-focus:bg-primary/10 group-focus:text-primary">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="min-w-0 flex-1 truncate text-xs font-medium">{label}</span>
      {badge ? <span className="grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1.5 font-mono text-[9px] font-semibold text-primary-foreground">{badge}</span> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/55 transition-transform group-focus:translate-x-0.5" />}
    </DropdownMenuItem>
  );
}
